<template>
    <div class="h-full w-full flex flex-col p-2 gap-2 payments-view">
        <UInput
            v-model="searchText"
            placeholder="Søk etter konto, beløp eller dato"
            icon="i-ph-magnifying-glass"
            type="search"
            @update:model-value="applySearchText"
        />
        <div class="flex w-full items-center mr-1 gap-x-2 justify-end">
            <div>Sum valgte: {{ formatCurrency(totalSelected) }}</div>
            <UButton
                size="sm"
                :disabled="paymentSelection.length === 0 || paymentSelection.length > 20 || loading"
                :loading="loading"
                @click="approveSelectedPayments"
            >
                <span>Godkjenn valgte</span>
            </UButton>
            <label for="showCompleted" class="cursor-pointer">Vis fullførte</label>
            <input
                id="showCompleted"
                v-model="showCompleted"
                type="checkbox"
                class="toggle toggle-primary"
            >
        </div>
        <ag-grid-vue
            v-if="gridVisible"
            class="w-full h-full py-4"
            :theme="agTheme"
            :column-defs="paymentColumns"
            :row-data="extendedPendingTransactions"
            :grid-options="gridOptions"
            data-cy="pending-transactions"
        />
        <pending-transaction-editor ref="pendingTransactionsEditorInstance" @success="fetch"/>
    </div>
</template>

<script setup lang="ts">
import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef} from 'vue'
import {getPaymentBasket, initiateExternalPayment, usePendingPayments} from '@/bank/payment.service'
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    RowDoubleClickedEvent,
    SelectionChangedEvent,
    ValueFormatterParams,
    ValueGetterParams
} from 'ag-grid-community'
import Fuse from 'fuse.js'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {router} from '@/outerRouter'
import {PAYMENT_APPROVED, PAYMENT_CREATED, PAYMENT_UPDATED} from '@/bank/bank.events'
import {updatePendingTransaction} from '@/pending-transaction/pendingTransaction.service'
import PendingTransactionAccountCell from '@/bank/PendingTransactionAccountCell.vue'
import {ExtendedPendingTransaction} from '@/pending-transaction/pendingTransaction.types'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import SubAccountInvoiceLinkCell from '@/subaccount/outstandingBalance/SubAccountInvoiceLinkCell.vue'
import TransactionLinkCell from '@/transaction/TransactionLinkCell.vue'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import TransactionAttachmentsCell from '@/transaction/TransactionAttachmentsCell.vue'
import PendingTransactionEditor from '@/bank/PendingTransactionEditor.vue'
import PaymentActions from '@/bank/PaymentActions.vue'
import {differenceInCalendarDays, formatDate} from 'date-fns'
import {todayDate} from '@/dateUtils/dateUtils'
import {globalDefaultColDef} from '@/agUtils'
import {notifyError} from '@/services/infrastructure/notification.service'
import {useI18n} from 'vue-i18n'

const {fetch, payments, showCompleted, pendingTransactions, extendedPendingTransactions} = usePendingPayments()

const pendingTransactionsEditorInstance = ref<InstanceType<typeof PendingTransactionEditor> | null>(null)
const loading = ref<boolean>(false)

type AgCellParam = {
    value?: any,
    data: ExtendedPendingTransaction
}

const {onEvents, cleanUp} = useEmitter()
const {t} = useI18n()

const gridApi = shallowRef<GridApi>()
const gridVisible = ref<boolean>(false)
const paymentSelection = ref<Array<ExtendedPendingTransaction>>([])
const totalSelected = computed(() => {
    return paymentSelection.value.reduce((acc, payment) => {
        return !payment?.amount ? acc : acc + payment.amount
    }, 0)
})
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const searchText = ref<string>('')

const paymentColumns: ColDef[] = [
    {
        headerName: 'Dato',
        width: 100,
        valueGetter: (params: ValueGetterParams) => params.data.date,
        valueFormatter: (params: ValueFormatterParams) => formattedDate(params.data.date)
    },
    {
        headerName: 'Avsender konto',
        flex: 3,
        autoHeight: true,
        cellRenderer: PendingTransactionAccountCell,
        cellRendererParams: ({data}: ICellRendererParams) => {
            return {
                data,
                isCredit: true
            }
        }
    },
    {
        headerName: 'Mottaker konto',
        flex: 3,
        autoHeight: true,
        cellRenderer: PendingTransactionAccountCell,
        cellRendererParams: ({data}: ICellRendererParams) => {
            return {
                data,
                isCredit: false
            }
        }
    },
    {
        headerName: 'Beløp',
        width: 120,
        field: 'amount',
        valueFormatter: (param: ValueFormatterParams) => formatCurrency(param.value),
        type: 'numericColumn'
    },
    {
        headerName: 'Bilag',
        width: 120,
        cellRenderer: TransactionLinkCell
    },
    {
        headerName: 'Vedlegg',
        valueGetter: (params: ValueGetterParams) => params.data.transactionInfo,
        width: 80,
        cellRenderer: TransactionAttachmentsCell
    },
    {
        headerName: 'Faktura',
        width: 120,
        cellRenderer: SubAccountInvoiceLinkCell
    },
    {
        headerName: 'Forfall',
        width: 100,
        valueGetter: (params: ValueGetterParams) => params.data.invoiceDueDate,
        valueFormatter: (params: ValueFormatterParams) => formattedDate(params.data.invoiceDueDate)
    },
    {
        headerName: 'Status',
        width: 100,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.paymentStatus === 'Completed' && params.data.status === 'Pending') return 'OutOfSync'
            return params.data.paymentStatus
        },
        valueFormatter: (params: ValueFormatterParams) => !params.data.paymentStatus ? '' : t(`bank.status.${params.data.paymentStatus}`)
    },
    {
        headerName: 'Valg',
        width: 90,
        sortable: false,
        filter: false,
        cellRenderer: PaymentActions
    }
]

const filterRowsBySearch = (node: IRowNode) => {
    const payment = node.data as ExtendedPendingTransaction
    const creditAccountName = findAccountByNo(payment.creditAccountNo)?.name.toLowerCase()
    if (searchText.value === '')
        return true
    if (payment.creditAccountNo.startsWith(searchText.value)
        || creditAccountName.startsWith(searchText.value)
        || payment.debitAccountNo.startsWith(searchText.value)
        || payment.debitSubAccountName?.toLowerCase().startsWith(searchText.value)) {
        return true
    }

    const fuse = new Fuse([
        formattedDate(payment.date),
        payment.creditAccountNo,
        creditAccountName,
        formatCurrency(payment.amount),
        payment.debitAccountNo,
        payment.debitSubAccountName?.toLowerCase()
    ], {includeScore: true, threshold: 0.3})

    return !!fuse.search(searchText.value).length
}

const applySearchText = () => {
    if (gridApi.value) {
        gridApi.value.onFilterChanged()
    }
}

const gridOptions: GridOptions = {
    animateRows: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => 'Ingen betalinger å vise'
    },
    getRowId: (params: any) => params.data.uuid,
    suppressCellFocus: true,
    context: {
        panelId,
        panelPathSegment
    },
    defaultColDef: {
        ...globalDefaultColDef,
        resizable: true,
        sortable: true

    },
    rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        isRowSelectable: (params: AgCellParam) => params.data.paymentStatus == null || params.data.paymentStatus.toLowerCase() === 'unsigned',
        selectAll: 'filtered'
    },
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: filterRowsBySearch,
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    onSelectionChanged(event: SelectionChangedEvent) {
        paymentSelection.value = event.api.getSelectedRows()
    },
    onRowDoubleClicked(event: RowDoubleClickedEvent<ExtendedPendingTransaction>) {
        pendingTransactionsEditorInstance.value?.show(event.data)
    }
}

const approveSelectedPayments = async () => {
    loading.value = true

    await checkAndUpdatePaymentDate(paymentSelection.value)

    initiateExternalPayment({
        scaRedirectBaseUrl: window.location.href.split('?')[0],
        paymentUuids: paymentSelection.value.map(payment => payment.paymentUuid)
    })
        .then((data) => {
            if (data.scaUrl) {
                window.location.href = data.scaUrl
            }
        })
        .catch((err) => {
            const data = err.response?.data
            let message: string
            if (data && data.code === 2202) {
                message = 'Du må opprette nytt samtykke for å godkjenne betalinger'
            } else {
                message = `${data.code} - ${data.detail}`
            }
            notifyError({title: 'Feilet', description: `Feilet initiering av betaling:\n ${message}`})
        })
        .finally(() => {
            loading.value = false
        })
    // if at some point we do not redirect to scaUrl, we should set loading.value = false and call fetch() here
}

const checkAndUpdatePaymentDate = async (payments: ExtendedPendingTransaction[]) => {
    for (const payment of payments) {
        if (differenceInCalendarDays(new Date(payment.date), todayDate) < 0) {
            await updatePendingTransaction({
                pendingTransactionUuid: payment.uuid,
                date: formatDate(todayDate, 'yyyy-MM-dd'),
                amount: payment.amount,
                creditAccountNo: payment.creditAccountNo
            })
        }
    }
}

const handleIncomingSCAStatus = async () => {
    const paymentBasketUuid = router.currentRoute.value.query.paymentBasketUuid
    const scaStatus = router.currentRoute.value.query.sca
    const status = router.currentRoute.value.query.status
    if (paymentBasketUuid && (scaStatus || status)) {
        await getPaymentBasket(paymentBasketUuid as string)
        emitter.emit(PAYMENT_APPROVED)
        await router.replace({query: {}})
    }
}

const clear = () => {
    searchText.value = ''
    paymentSelection.value = []
    payments.value = []
    pendingTransactions.value = []
}

onMounted(async () => {
    await handleIncomingSCAStatus()
    await fetch()
    onEvents([ORG_SELECTED, ORG_SWITCHED], () => {
        clear()
        fetch()
    })
    onEvents([PAYMENT_CREATED, PAYMENT_APPROVED, PAYMENT_UPDATED], () => {
        fetch()
    })
    onEvents([NO_ORG_SELECTED, LOGOUT], clear)
    gridVisible.value = true
})
onUnmounted(cleanUp)
</script>

<style>
.payments-view {
    .ag-checkbox-input-wrapper.ag-disabled {
        display: none;
    }
}

</style>